import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import {
  selectPreferences,
  getPreferencesThunk,
  selectLoadingPreferences,
  selectErrorPreferences,
} from '../store/preferencesSlice';
import {
  selectProfile,
  getProfileThunk,
  selectLoadingProfile,
  selectErrorProfile,
} from '../store/profileSlice';
import { useAppDispatch } from '.';
import { setUserToken, logLastLoginThunk } from '../store/authSlice';
import { loginRequest } from '../components/preAuthentification/msalConfig';

export interface DataInitialization {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

export function useDataInitialization(): DataInitialization {
  const { accounts, instance } = useMsal();
  const dispatch = useAppDispatch();
  const profile = useSelector(selectProfile);
  const preferences = useSelector(selectPreferences);
  
  const isProfileLoading = useSelector(selectLoadingProfile);
  const isPreferencesLoading = useSelector(selectLoadingPreferences);
  const profileError = useSelector(selectErrorProfile);
  const preferencesError = useSelector(selectErrorPreferences);

  useEffect(() => {
    const initializeAuth = async () => {
      // Handle redirect promise first
      await instance.handleRedirectPromise();

      if (accounts[0]) {
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });

          dispatch(setUserToken(response.accessToken));
          dispatch(getProfileThunk());
          dispatch(getPreferencesThunk());

          const authTime = Math.floor(Date.now() / 1000);
          dispatch(logLastLoginThunk(authTime));
        } catch (error) {
          console.error('Token acquisition failed:', error);
          // If silent token acquisition fails, try interactive
          try {
            await instance.loginRedirect(loginRequest);
          } catch (redirectError) {
            console.error('Login redirect failed:', redirectError);
          }
        }
      }
    };

    initializeAuth();
  }, [accounts, instance, dispatch]);

  const isLoading = useMemo(
    () => isProfileLoading || isPreferencesLoading,
    [isProfileLoading, isPreferencesLoading]
  );

  const isError = useMemo(
    () => Boolean(profileError) || Boolean(preferencesError),
    [profileError, preferencesError]
  );

  const isSuccess = useMemo(() => !isLoading && !isError, [isLoading, isError]);

  return { isLoading, isError, isSuccess };
}
